import React, { useEffect, useState } from 'react';
import { Modal } from 'bootstrap';
// The slide-in dialog's layout rules live here; import them rather than relying on another
// page having pulled the stylesheet in.
import '../pages/collections/modal.css';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useCreateLeadWithChannel } from '../../services/createLeadWithChannel';
import { useSearchCustomers, useGetCustomer } from '../../services/customerService';
import { Customer } from '../../services/schemas/customerServiceSchemas';
import { ContactDetailCreateContactType } from '../../services/schemas/customerServiceSchemas/contactDetailCreateContactType';
import type { SearchCustomersParams } from '../../services/schemas/customerServiceSchemas/searchCustomersParams';
import type { GetCustomerParams } from '../../services/schemas/customerServiceSchemas/getCustomerParams';
import { LeadLoanType } from '../../services/schemas/loanServiceSchemas';
import LeadForm, { LeadFormValues, emptyLeadFormValues, toLeadPayload } from './LeadForm';

type SearchType = 'registrationNumber' | 'customerName' | 'idNumber';

const SEARCH_OPTIONS: Record<SearchType, { icon: string; title: string; hint: string; placeholder: string }> = {
    registrationNumber: { icon: 'bi-hash', title: 'Registration Number', hint: 'Search by company reg number', placeholder: 'Enter company registration number...' },
    customerName: { icon: 'bi-building', title: 'Company Name', hint: 'Search by registered name', placeholder: 'Enter registered company name...' },
    idNumber: { icon: 'bi-person-vcard', title: 'ID Number', hint: 'Search by South African ID number', placeholder: 'Enter 13-digit ID number...' }
};

const CreateLead: React.FC = () => {
    const queryClient = useQueryClient();
    const user = useSelector((state: RootState) => state.auth.user);
    const [currentStep, setCurrentStep] = useState<'search' | 'form'>('search');
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    // Chosen first, because it decides who to search for: a company or an individual.
    const [loanType, setLoanType] = useState<LeadLoanType>(LeadLoanType.BUSINESS);
    const [formValues, setFormValues] = useState<LeadFormValues>(emptyLeadFormValues());
    // Remounts the form whenever its starting values change, so it never shows a previous lead.
    const [formKey, setFormKey] = useState(0);
    const [submitError, setSubmitError] = useState<string | undefined>();

    const [searchType, setSearchType] = useState<SearchType>('registrationNumber');
    const [searchValue, setSearchValue] = useState('');
    const [searchParams, setSearchParams] = useState<SearchCustomersParams | undefined>();
    const [getCustomerParams, setGetCustomerParams] = useState<GetCustomerParams | undefined>();
    const [searchResults, setSearchResults] = useState<Customer[]>([]);
    const [hasSearched, setHasSearched] = useState(false);

    const isBusiness = loanType === LeadLoanType.BUSINESS;

    // Corporate customer search query
    const { data: searchData, isLoading: isSearchLoading, error: searchError, refetch: refetchSearch } = useSearchCustomers(
        searchParams,
        { query: { enabled: false } }
    ) as { data: any; isLoading: boolean; error: any; refetch: () => void };

    // Individual customer lookup by ID number
    const { data: customerData, isLoading: isCustomerLoading, error: customerError, refetch: refetchCustomer } = useGetCustomer(
        getCustomerParams,
        { query: { enabled: false } }
    ) as { data: Customer | undefined; isLoading: boolean; error: any; refetch: () => void };

    const isSearchingCustomer = isSearchLoading || isCustomerLoading;

    useEffect(() => {
        if (searchData && !isSearchLoading) {
            setSearchResults(searchData?.content || []);
            setHasSearched(true);
        }
    }, [searchData, isSearchLoading]);

    useEffect(() => {
        if (customerData && !isCustomerLoading) {
            setSearchResults(customerData ? [customerData] : []);
            setHasSearched(true);
        }
    }, [customerData, isCustomerLoading]);

    useEffect(() => {
        if ((searchError || customerError) && !isSearchingCustomer) {
            setSearchResults([]);
            setHasSearched(true);
        }
    }, [searchError, customerError, isSearchingCustomer]);

    const createLeadMutation = useCreateLeadWithChannel({
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['/loan/api/v1/leads'] });

            const modalElement = document.getElementById('CreateLead');
            if (modalElement) {
                Modal.getInstance(modalElement)?.hide();
            }

            handleModalReset();
        },
        onError: (error: any) => {
            console.error('Error creating lead:', error);
            setSubmitError(error?.response?.data?.message || error?.message || 'The lead could not be created.');
        }
    });

    const startForm = (values: LeadFormValues) => {
        setFormValues(values);
        setFormKey(key => key + 1);
        setSubmitError(undefined);
        setCurrentStep('form');
    };

    const clearSearch = () => {
        setSearchValue('');
        setSearchResults([]);
        setHasSearched(false);
        setSearchParams(undefined);
        setGetCustomerParams(undefined);
    };

    const handleModalReset = () => {
        setCurrentStep('search');
        setSelectedCustomer(null);
        setLoanType(LeadLoanType.BUSINESS);
        setSearchType('registrationNumber');
        setSubmitError(undefined);
        clearSearch();
        setFormValues(emptyLeadFormValues());
        setFormKey(key => key + 1);
    };

    // Listen for modal events
    useEffect(() => {
        const modalElement = document.getElementById('CreateLead');
        if (!modalElement) return;

        const handleModalHidden = () => {
            handleModalReset();
        };

        modalElement.addEventListener('hidden.bs.modal', handleModalHidden);
        return () => {
            modalElement.removeEventListener('hidden.bs.modal', handleModalHidden);
        };
    }, []);

    const handleLoanTypeChange = (type: LeadLoanType) => {
        setLoanType(type);
        setSearchType(type === LeadLoanType.BUSINESS ? 'registrationNumber' : 'idNumber');
        clearSearch();
    };

    const handleSearch = () => {
        if (!searchValue.trim()) return;

        setHasSearched(false);
        setSearchResults([]);
        setSearchParams(undefined);
        setGetCustomerParams(undefined);

        if (searchType === 'idNumber') {
            setGetCustomerParams({ idNumber: searchValue.trim() });
            setTimeout(() => {
                refetchCustomer();
            }, 100);
            return;
        }

        const params: SearchCustomersParams = {};
        if (searchType === 'registrationNumber') {
            params.registrationNumber = searchValue.trim();
        } else {
            params.registeredName = searchValue.trim();
        }

        setSearchParams(params);
        setTimeout(() => {
            refetchSearch();
        }, 100);
    };

    const handleSkipSearch = () => {
        setSelectedCustomer(null);
        startForm(emptyLeadFormValues(loanType));
    };

    const handleSelectCustomer = (customer: Customer) => {
        const emailContact = customer.contacts?.find(contact =>
            contact.contactType === ContactDetailCreateContactType.EMAIL
        );

        const isPhone = (type?: string) =>
            type === ContactDetailCreateContactType.MOBILE ||
            type === ContactDetailCreateContactType.WHATSAPP ||
            type === ContactDetailCreateContactType.SMS;
        const phoneContact = customer.contacts?.find(contact => isPhone(contact.contactType) && contact.primary === true)
            || customer.contacts?.find(contact => isPhone(contact.contactType));

        const contact = {
            email: emailContact?.contactValue || '',
            primaryPhone: phoneContact?.contactValue || ''
        };
        const info = customer.info as any;

        if (customer.type === 'CORPORATE' && info) {
            setSelectedCustomer(customer);
            startForm({
                ...emptyLeadFormValues(LeadLoanType.BUSINESS),
                ...contact,
                existingCustomer: true,
                legalEntityType: info.legalEntityType || '',
                registrationNumber: info.registrationNumber || '',
                registeredName: info.registeredName || '',
                firstName: info.representativeFirstName || '',
                lastName: info.representativeLastName || ''
            });
        } else if (info) {
            setSelectedCustomer(customer);
            startForm({
                ...emptyLeadFormValues(LeadLoanType.PERSONAL),
                ...contact,
                existingCustomer: true,
                firstName: info.firstName || '',
                lastName: info.lastName || '',
                idNumber: info.idNumber || '',
                representativeOccupation: info.occupation || ''
            });
        }
    };

    const onSubmit = (values: LeadFormValues) => {
        setSubmitError(undefined);
        // The channel travels as a request header, not in the body.
        createLeadMutation.mutate({
            // Recorded as the lead's creator, instead of the login id.
            data: { ...toLeadPayload(values), recordedBy: user?.name || user?.username },
            channel: values.channel
        });
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const searchOptions: SearchType[] = isBusiness ? ['registrationNumber', 'customerName'] : ['idNumber'];
    const noun = isBusiness ? 'company' : 'customer';

    const resultTitle = (customer: Customer) => {
        const info = customer.info as any;
        return customer.type === 'CORPORATE'
            ? info?.registeredName || 'N/A'
            : `${info?.firstName || ''} ${info?.lastName || ''}`.trim() || 'N/A';
    };

    const resultIdentifier = (customer: Customer) => {
        const info = customer.info as any;
        return customer.type === 'CORPORATE'
            ? { label: 'Registration No.', value: info?.registrationNumber }
            : { label: 'ID Number', value: info?.idNumber };
    };

    return (
        <div className="modal-content rounded-0 border-0 shadow-lg">
            {currentStep === 'search' ? (
                <>
                    {/* Header */}
                    <div className="modal-header border-bottom pb-0" style={{ padding: '2rem 2rem 1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                        <div className="w-100">
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <div className="d-flex align-items-center">
                                    <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                                        <i className="fa fa-search fa-2x text-primary" style={{ display: 'block' }}></i>
                                    </div>
                                    <div>
                                        <h4 className="modal-title mb-0 text-dark fw-bold">Create New Lead</h4>
                                        <p className="text-muted mb-0 mt-1 small">Choose the loan type, then search for an existing customer or skip to enter details</p>
                                    </div>
                                </div>
                                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                            </div>
                        </div>
                    </div>

                    {/* Body - Customer Search */}
                    <div className="modal-body p-4" style={{ backgroundColor: '#f8f9fa' }}>
                        {/* Loan Type */}
                        <div className="card border-0 shadow-sm mb-4 flex-shrink-0">
                            <div className="card-body p-4">
                                <h5 className="fw-bold mb-3">
                                    <i className="bi bi-briefcase me-2 text-primary"></i>
                                    Loan Type
                                </h5>
                                <div className="btn-group w-100" role="group" aria-label="Loan type">
                                    <button
                                        type="button"
                                        className={`btn py-3 ${isBusiness ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => handleLoanTypeChange(LeadLoanType.BUSINESS)}
                                        aria-pressed={isBusiness}
                                    >
                                        <i className="fa fa-building me-2"></i>
                                        Business loan
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn py-3 ${!isBusiness ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => handleLoanTypeChange(LeadLoanType.PERSONAL)}
                                        aria-pressed={!isBusiness}
                                    >
                                        <i className="fa fa-user me-2"></i>
                                        Personal loan
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Search Section */}
                        <div className="card border-0 shadow-sm mb-4 flex-shrink-0">
                            <div className="card-body p-4">
                                <div className="mb-4">
                                    <h5 className="fw-bold mb-2">
                                        <i className="bi bi-search me-2 text-primary"></i>
                                        {isBusiness ? 'Search for Company' : 'Search for Customer'}
                                    </h5>
                                    <p className="mb-0 text-muted small">Find an existing {noun} to pre-populate lead information</p>
                                </div>

                                {/* Search Type Selection - a compact toggle, so the search box stays in view */}
                                {searchOptions.length > 1 && (
                                    <div className="btn-group w-100 mb-3" role="group" aria-label="Search by">
                                        {searchOptions.map(option => {
                                            const meta = SEARCH_OPTIONS[option];
                                            const selected = searchType === option;
                                            return (
                                                <button
                                                    key={option}
                                                    type="button"
                                                    className={`btn ${selected ? 'btn-primary' : 'btn-outline-primary'}`}
                                                    onClick={() => setSearchType(option)}
                                                    aria-pressed={selected}
                                                    title={meta.hint}
                                                >
                                                    <i className={`bi ${meta.icon} me-2`}></i>
                                                    {meta.title}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Search Input */}
                                <div className="mb-3">
                                    <div className="position-relative">
                                        <div className="input-group input-group-lg shadow-sm" style={{ borderRadius: '0.75rem', overflow: 'hidden', border: '1px solid #dee2e6' }}>
                                            <span className="input-group-text bg-white border-0 px-4">
                                                <i className={`bi ${isBusiness ? 'bi-building' : 'bi-person'} text-primary`} style={{ fontSize: '1.25rem' }}></i>
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control border-0 py-3"
                                                placeholder={SEARCH_OPTIONS[searchType].placeholder}
                                                value={searchValue}
                                                onChange={(e) => setSearchValue(e.target.value)}
                                                onKeyPress={handleKeyPress}
                                                style={{ fontSize: '1.05rem', fontWeight: '500' }}
                                            />
                                            {searchValue && (
                                                <button
                                                    className="btn border-0 px-3"
                                                    type="button"
                                                    onClick={clearSearch}
                                                    style={{ background: 'white' }}
                                                >
                                                    <i className="bi bi-x-circle text-muted"></i>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Search Actions - not sticky: pinned to the bottom it sat over the search box */}
                                <div className="d-flex gap-3">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary px-4 py-2 rounded-pill flex-grow-1 fw-semibold"
                                        onClick={handleSkipSearch}
                                    >
                                        <i className="bi bi-skip-forward me-2"></i>
                                        Skip & Enter Manually
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary px-5 py-2 rounded-pill fw-bold shadow"
                                        disabled={isSearchingCustomer || !searchValue.trim()}
                                        onClick={handleSearch}
                                        style={{ minWidth: '150px' }}
                                    >
                                        {isSearchingCustomer ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Searching...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-search me-2"></i>
                                                Search
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Search Results */}
                        {hasSearched && !isSearchingCustomer && (
                            <div className="card border-0 shadow-sm flex-shrink-0">
                                <div className="card-body p-0">
                                    <div className="p-4 pb-3 border-bottom bg-light">
                                        <h6 className="mb-1 fw-bold">
                                            <i className="bi bi-list-ul me-2 text-primary"></i>
                                            Search Results
                                        </h6>
                                        <small className="text-muted">
                                            {searchResults.length > 0
                                                ? `Found ${searchResults.length} ${isBusiness ? (searchResults.length !== 1 ? 'companies' : 'company') : (searchResults.length !== 1 ? 'customers' : 'customer')}`
                                                : `No ${isBusiness ? 'companies' : 'customers'} found`}
                                        </small>
                                    </div>

                                    {searchResults.length > 0 ? (
                                        <div className="p-3">
                                            {searchResults.map((customer, index) => {
                                                const identifier = resultIdentifier(customer);
                                                return (
                                                    <div
                                                        key={index}
                                                        className="card border mb-3 hover-shadow flex-shrink-0"
                                                        style={{ transition: 'all 0.2s ease', cursor: 'default' }}
                                                    >
                                                        <div className="card-body p-3">
                                                            <div className="row align-items-center">
                                                                <div className="col-md-8">
                                                                    <div className="d-flex align-items-start">
                                                                        <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f0f7ff', border: '2px solid #e3f2fd' }}>
                                                                            <i className={`bi ${customer.type === 'CORPORATE' ? 'bi-building' : 'bi-person'} text-primary`} style={{ fontSize: '1.25rem' }}></i>
                                                                        </div>
                                                                        <div className="flex-grow-1">
                                                                            <h6 className="mb-1 fw-bold">{resultTitle(customer)}</h6>
                                                                            <div className="d-flex flex-wrap gap-3 mt-2">
                                                                                <div>
                                                                                    <small className="text-muted d-block">{identifier.label}</small>
                                                                                    <span className="badge bg-light text-dark border">
                                                                                        <i className="bi bi-hash me-1"></i>
                                                                                        {identifier.value || 'N/A'}
                                                                                    </span>
                                                                                </div>
                                                                                {customer.contacts && customer.contacts.length > 0 && (
                                                                                    <div>
                                                                                        <small className="text-muted d-block">Contact</small>
                                                                                        <span className="badge bg-light text-dark border">
                                                                                            <i className="bi bi-telephone me-1"></i>
                                                                                            {customer.contacts[0]?.contactValue || 'N/A'}
                                                                                        </span>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="col-md-4 text-md-end mt-3 mt-md-0">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-primary px-4 py-2 rounded-pill shadow-sm"
                                                                        onClick={() => handleSelectCustomer(customer)}
                                                                    >
                                                                        <i className="bi bi-check-circle me-2"></i>
                                                                        {isBusiness ? 'Select Company' : 'Select Customer'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-center py-5">
                                            <div className="mb-3">
                                                <i className="bi bi-inbox text-muted" style={{ fontSize: '3rem', display: 'block' }}></i>
                                            </div>
                                            <h6 className="text-muted mb-2">No {isBusiness ? 'Companies' : 'Customers'} Found</h6>
                                            <p className="text-muted mb-4 small">
                                                No {isBusiness ? 'companies' : 'customers'} match your search criteria.<br />
                                                Try a different search or create a new lead manually.
                                            </p>
                                            <button
                                                type="button"
                                                className="btn btn-primary px-4 py-2 rounded-pill"
                                                onClick={handleSkipSearch}
                                            >
                                                <i className="bi bi-plus-circle me-2"></i>
                                                Create New Lead
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <>
                    {/* Header */}
                    <div className="modal-header border-bottom pb-0" style={{ padding: '2rem 2rem 1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                        <div className="w-100">
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <div className="d-flex align-items-center">
                                    <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                                        <i className="fa fa-user-plus fa-2x text-primary" style={{ display: 'block' }}></i>
                                    </div>
                                    <div>
                                        <h4 className="modal-title mb-0 text-dark fw-bold">Lead Details</h4>
                                        <p className="text-muted mb-0 mt-1 small">
                                            {selectedCustomer ? 'Customer data pre-populated' : 'Enter new lead information'}
                                        </p>
                                    </div>
                                </div>
                                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                            </div>

                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary mb-3"
                                onClick={() => {
                                    setSelectedCustomer(null);
                                    setCurrentStep('search');
                                }}
                            >
                                <i className="bi bi-arrow-left me-2"></i>
                                Back to Search
                            </button>
                        </div>
                    </div>

                    {/* Body - Lead Form */}
                    <div className="modal-body p-4" style={{ backgroundColor: '#f8f9fa' }}>
                        <LeadForm
                            key={formKey}
                            mode="create"
                            defaultValues={formValues}
                            lockIdentity={!!selectedCustomer}
                            isPending={createLeadMutation.isPending}
                            errorMessage={submitError}
                            onSubmit={onSubmit}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default CreateLead;
